<script setup lang="ts">
import { computed, onMounted, ref, toRefs } from 'vue';
import { useDisplay } from 'vuetify';
import { VCard, VDataTable, VDataTableVirtual } from 'vuetify/components';
import AddButton from './AddButton.vue';
import { useInfiniteScroll } from '@vueuse/core';
import useLocale from '../helpers/use-locale';
import type { Content } from '@ilteducation/content-registry-types';

type ReadonlyHeaders = VDataTableVirtual['headers'];

type Emits = {
  (e: 'openContent', id: string, content: Content): void;
  (e: 'addContent', content: string[]): void;
  (e: 'removeContent'): void;
  (e: 'onLoadContent'): void;
  (e: 'update:modelValue', value: string[]): void;
};
const emit = defineEmits<Emits>();

type Props = {
  modelValue: string[]; // selected via checkboxes
  rounded?: boolean; // adds css rounding
  bordered?: boolean;
  contentItems: any[]; // items for datatable
  heads: ReadonlyHeaders;
  parentScrollElement?: HTMLElement | null; // parent element for infinite scroll
  selectAllContent?: (isAllSelected: boolean, selectAllDefault: (value: boolean) => void) => void; // callback function to select all content in case of custom needs
  disabledToAddContent?: string[]; // content that already added in collection
  isLoading?: boolean;
  fixedHeader?: boolean;
  hasMoreContent?: boolean;
  isHistoryCollection?: boolean; //Will be cast to false if it is absent.
  preventLoad?: boolean;
  isSelecting?: boolean; // whole row toggles the selection instead of opening the item
};

const props = withDefaults(defineProps<Props>(), {
  contentItems: () => [],
  parentScrollElement: null,
});
const { modelValue, contentItems, parentScrollElement, disabledToAddContent, heads } = toRefs(props);

const { t, locale } = useLocale();
const { xs } = useDisplay();

const onLoad = () => {
  emit('onLoadContent');
};

onMounted(() => {
  if (parentScrollElement?.value) {
    useInfiniteScroll(parentScrollElement, onLoad, { distance: 20, canLoadMore: () => !props.preventLoad });
  }
});

const updateModel = (value: string[]) => {
  emit('update:modelValue', value);
};

const toggleItemSelection = (item: any) => {
  const selected = modelValue.value ?? [];
  updateModel(selected.includes(item.id) ? selected.filter((id) => id !== item.id) : [...selected, item.id]);
};

/**
 * In select mode the entire row is the target, so a tap does not have to land on the checkbox and
 * never opens the item by mistake.
 */
const onRowClick = (_event: unknown, row: { item: any }) => onItemClick(row.item);

const onItemClick = (item: any) => {
  if (props.isSelecting) {
    toggleItemSelection(item);
    return;
  }
  emit('openContent', item.id, item);
};
const doSelectAll = (allSelected: boolean, defaultSelectAll: (allSelected: boolean) => void) => {
  const maxSelectedItems = 120;
  if (allSelected) {
    updateModel([]);
    return;
  }
  if (typeof props.selectAllContent === 'function') {
    props.selectAllContent(allSelected, defaultSelectAll);
    return;
  }
  if (contentItems.value.length > maxSelectedItems) {
    const selected = contentItems.value.slice(0, maxSelectedItems).map((c) => c.id);
    updateModel(selected);
    return;
  }
  defaultSelectAll(!allSelected);
};

const doToggleSort = (column: any, callback: (column: any) => void) => {
  if (!column?.sortable) {
    return;
  }
  callback(column);
};

const addContent = (contentId: string) => {
  if (contentId) {
    emit('addContent', [contentId]);
    return;
  }
  emit('addContent', modelValue.value);
  updateModel([]);
};
/**
 * A row already in the collection is not selectable, and it used to render a ticked, disabled
 * checkbox in the select column — which reads as "selected", so a 130-reference collection looked
 * like 130 selected rows beside a count of 10. It carries the same "added" marker as the add column.
 */
const isContentInAdded = (contentId: string) => {
  if (!disabledToAddContent.value || disabledToAddContent.value.length === 0) {
    return false;
  }
  return disabledToAddContent.value.includes(contentId);
};

const getCustomSort =
  () =>
  (a: any, b: any): number => {
    // Handle numeric values (treat undefined as -1 for sorting)
    if (typeof a === 'number' || typeof b === 'number') {
      const numA = a !== undefined ? a : -1;
      const numB = b !== undefined ? b : -1;
      return numA - numB;
    }
    // Use localeCompare for strings
    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b, locale(), {
        sensitivity: 'base',
        numeric: true,
      });
    }

    return a > b ? 1 : a < b ? -1 : 0;
  };

const customKeySort = computed(() =>
  heads.value?.reduce(
    (acc: Record<string, any>, header: any) => ({
      ...acc,
      // @ts-ignore
      [header.key]: getCustomSort(),
    }),
    {},
  ),
);

const tableRef = ref<InstanceType<typeof VDataTable> | null>(null);
const tableSortBy = ref([]);
</script>

<template>
  <div ref="wrapperRef" class="content-table-wrapper">
    <v-data-table
      ref="tableRef"
      :model-value="modelValue"
      :fixed-header="fixedHeader"
      :item-selectable="(item: any) => !isContentInAdded(item.id)"
      :headers="heads as ReadonlyHeaders"
      :items="contentItems"
      :loading="isLoading"
      class="content-table"
      :class="{ bordered: bordered }"
      :no-data-text="t('components.no_data_available')"
      :loading-text="t('components.loading_items')"
      :custom-key-sort="customKeySort"
      :row-props="() => ({ class: isSelecting ? 'cursor-pointer' : undefined })"
      @update:modelValue="updateModel"
      @click:row="onRowClick"
      :items-per-page="-1"
      v-model:sortBy="tableSortBy"
    >
      <template #[`headers`]="{ columns, isSorted, getSortIcon, toggleSort, selectAll, allSelected }">
        <tr>
          <template v-for="column in columns" :key="column.key">
            <th
              v-if="column.key === 'data-table-select'"
              class="bg-transparent mr-2"
              scope="col"
              :class="`text-${column.align}`"
              :style="`min-width: ${column.width}`"
            >
              <v-btn
                v-if="!isHistoryCollection"
                class="pa-0 px-1"
                :class="{
                  'rounded-lg': rounded,
                  'rounded-sm': !rounded,
                }"
                :disabled="isLoading"
                variant="text"
                @click="doSelectAll(allSelected, selectAll)"
              >
                <span class="text-none select-all">
                  {{ allSelected ? t('components.deselect_all') : t('components.select_all') }}
                </span>
              </v-btn>
            </th>
            <th
              v-else
              class="bg-transparent mr-2"
              scope="col"
              :class="{
                'cursor-pointer': column.sortable,
                'text-center': column.align === 'center',
                'text-start': column.align === 'start',
              }"
              :style="`width: ${column.width}`"
              @click="doToggleSort(column, toggleSort)"
            >
              <span v-if="column?.title">{{ t(column.title) }}</span>
              <template v-if="column.sortable && isSorted(column)">
                <v-icon :icon="getSortIcon(column)"></v-icon>
              </template>
            </th>
          </template>
        </tr>
      </template>

      <template #[`item.data-table-select`]="{ item, isSelected, toggleSelect, internalItem }">
        <span v-if="!internalItem.selectable" class="text-on-tertiary-container text-no-wrap font-weight-bold">
          <v-icon>mdi-checkbox-marked-circle-outline</v-icon>
          <span v-if="!xs">{{ t('components.added') }}</span>
        </span>
        <v-checkbox-btn
          v-else
          :model-value="isSelected(internalItem)"
          :aria-label="t('components.select_item', { name: item.title })"
          @update:model-value="toggleSelect(internalItem)"
          @click.stop
        />
      </template>
      <template #[`item.id`]="{ item }">
        <v-card width="55" max-height="55" class="ma-2 ml-0" @click.stop="onItemClick(item)" :aria-label="item.title">
          <slot :item="item" :id="item.id" :title="item.title" name="itemCover"></slot>
        </v-card>
      </template>
      <template #[`item.title`]="{ item }">
        <div class="d-flex align-center">
          <slot :item="item" name="title-prepend"></slot>
          <slot v-if="item.properties.isSpeakingBook" name="prepend-label"></slot>
          <v-btn class="pl-0 on-surface" variant="text" flat @click.stop="onItemClick(item)">
            <span class="content-title text-none text-wrap text-left">{{ item.title }}</span>
          </v-btn>
        </div>
      </template>
      <template #[`item.addContent`]="{ item }">
        <div v-if="isContentInAdded(item.id)">
          <span class="text-on-tertiary-container text-no-wrap font-weight-bold">
            <v-icon>mdi-checkbox-marked-circle-outline</v-icon>
            <span v-if="!xs">{{ t('components.added') }}</span>
          </span>
        </div>
        <AddButton
          v-else
          :label="t('components.add_button_label', { title: item.title })"
          @click-add="addContent(item.id)"
        />
      </template>
      <template #[`item.authors`]="{ value }">
        <slot name="itemAuthors" :authors="value"></slot>
      </template>
      <template #[`item.duration`]="{ value }">
        <slot name="itemDuration" :duration="value"></slot>
      </template>
      <template #[`item.isbn`]="{ value }">
        <slot name="itemISBN" :isbn="value"></slot>
      </template>

      <template #bottom>
        <div v-if="hasMoreContent && !isLoading" class="text-center">
          <v-btn class="text-none" size="small" variant="outlined" color="primary" @click="onLoad">
            {{ t('show_button.more') }}
          </v-btn>
        </div>
        <div v-if="isLoading" class="d-flex justify-center">
          <v-progress-circular
            indeterminate
            :size="40"
            :aria-label="t('components.loading_items')"
          ></v-progress-circular>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

.select-btn {
  font-size: 1.1rem;
  font-weight: bold;
}

.content-table {
  background-color: transparent;
  overflow: hidden;
}

:deep(.v-table__wrapper) {
  overflow: visible;
}

:deep(.v-table.bordered > .v-table__wrapper > table) {
  border-collapse: collapse;
}

:deep(.v-selection-control) {
  justify-content: center;
}

:deep(.v-table:not(.bordered) .v-data-table__td) {
  border: none !important;
}

:deep(.v-data-table__th) {
  border-bottom: 1px solid #000 !important;
}

:deep(.v-table.bordered .v-data-table__tr) {
  border-bottom: 1px solid;
  border-bottom-color: rgba(var(--v-theme-outline-variant));
}

:deep(.v-table > .v-table__wrapper > table > thead > tr > th:first-child),
:deep(.v-table > .v-table__wrapper > table > tbody > tr > td:first-child) {
  padding-left: 0;
}

:deep(.v-table > .v-table__wrapper > table > thead > tr > th:last-child),
:deep(.v-table > .v-table__wrapper > table > tbody > tr > td:last-child) {
  padding-right: 0;
}

:deep(input) {
  outline: none !important;
}

.select-all {
  text-wrap: wrap;
}

@media #{map.get(v.$display-breakpoints, 'xs')} {
  :deep(.v-data-table__td) {
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 70px;
    overflow: hidden;
  }
  .select-all {
    font-size: 11px;
  }
}
</style>
